import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Responsive from 'react-responsive';
import { DeprecatedFAIcon } from '@components-library/Icons/DeprecatedFAIcon';
import { DEPRECATED_FA_GLOBAL_SPRITE } from '@components-library/Icons/interfaces';
import withRouterInnerRef from '@components-shared/withRouterInnerRef';
import { getSidebarMenuLabel } from '@utils/menuUtils';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';
import { Drawer } from 'components/shared/drawer/drawer';
import AppFooter from 'components/shared/footer';
import SignOut from 'components/shared/signOut';
import { DEVICE_ENUM } from 'global.enums';
import { DeviceType, IGeneralSettings, IOnboardingConfiguration, IUser } from 'global.interfaces';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import styleVariables from 'styles/user/variables.scss';
import UserPageHeader from '../Header';
import { MetaServerInfo } from './MetaServerInfo';
import { getMenuConfig } from './menuConfig';
import styles from './styles.module.scss';
import { UserSocialProfile } from './userSocialProfile';

type MenuItem = Required<MenuProps>['items'][number];

const { Sider } = Layout;
const SIDER_WIDTH = '246px';

type HiddenComponent = (data: Partial<IUser & IOnboardingConfiguration>) => boolean;

export type SubmenuItemType = {
  name: string;
  path?: string;
  isHidden?: HiddenComponent;
  onClick?: () => void;
};

export type MenuItemType = {
  name: string;
  type: string;
  path?: string;
  icon?: React.ReactNode;
  isHidden?: HiddenComponent;
  exact?: boolean;
  children?: Array<SubmenuItemType>;
  additionalMatchPaths?: Array<string>;
};

export type UserPageLayoutProps = {
  className?: string;
  user: IUser & {
    fullName?: string;
  };
  generalSettings: IGeneralSettings;
  onboardingConfiguration: IOnboardingConfiguration;
  children: React.ReactNode;
  handlerOpenNavMenu: () => void;
  documentDirection: string;
};

const drawerStyles: DrawerStyles = {
  body: {
    padding: 0,
  },
  wrapper: {
    width: SIDER_WIDTH,
  },
  mask: {
    background: 'rgba(11, 91, 184, 0.5)',
  },
};

const UserPageLayout = observer((props: UserPageLayoutProps) => {
  const { tradingAccountsStore, featuresStore } = useMobxStore();
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const { t } = useTranslation();

  const closeMobileMenuAfterChooseMenuItem = (deviceType: DEVICE_ENUM): void => {
    if (deviceType === DEVICE_ENUM.MOBILE) {
      toggleMobileMenu();
    }
  };

  const onMenuItemClick = (deviceType: DeviceType, handler?: () => void): void => {
    closeMobileMenuAfterChooseMenuItem(deviceType);

    if (handler) {
      handler();
    }
  };

  const locationMatchesPath = (path?: string): boolean => (path ? location.pathname.includes(path) : false);

  const locationMatchesMenuItem = (menuItem: MenuItemType): boolean => {
    if (menuItem.exact && location.pathname === menuItem.path) {
      return true;
    }

    if (!menuItem.exact && locationMatchesPath(menuItem.path)) {
      return true;
    }

    return !!(menuItem.additionalMatchPaths && menuItem.additionalMatchPaths.some((i) => locationMatchesPath(i)));
  };

  const getPathOfSelectedMenuItem = (menuWithoutEntryPath: Array<MenuItemType>): Array<string> => {
    const path: Array<string> = [];

    menuWithoutEntryPath.forEach((m) => {
      if (m.children) {
        m.children.forEach((child) => {
          if (locationMatchesPath(child.path)) {
            if (child.path) {
              path.push(child.path);
            }
          }
        });
        return path;
      }

      if (locationMatchesMenuItem(m)) {
        if (m.path) {
          path.push(m.path);
        }
        return path;
      }
    });

    return path;
  };

  const renderMenuItems = (menuItems: Array<MenuItemType>, deviceType: DeviceType): MenuItem[] => {
    return menuItems.map((menuItem) => {
      const children = menuItem.children
        ? menuItem.children.map((child) => {
            return {
              label: getSidebarMenuLabel({ name: child.name, path: child.path, t }),
              key: child.path,
              className: 'sidebar-panel__submenuItem',
              onClick: () => onMenuItemClick(deviceType, child.onClick),
            };
          })
        : undefined;

      return {
        label: getSidebarMenuLabel({ name: menuItem.name, path: menuItem.path, exact: menuItem.exact, t }),
        key: menuItem.path,
        icon: menuItem.icon,
        onClick: () => onMenuItemClick(deviceType),
        className: styles.menuItem,
        children,
      } as MenuItem;
    });
  };

  const renderMenu = (deviceType: DeviceType) => {
    const { user, onboardingConfiguration } = props;

    const menu = getMenuConfig(user, onboardingConfiguration, { tradingAccountsStore, featuresStore });
    const selectedPathName = getPathOfSelectedMenuItem(menu);
    const selectedMenuItem = selectedPathName.length > 0 ? getPathOfSelectedMenuItem(menu) : [''];
    const items = renderMenuItems(menu, deviceType);

    return (
      <>
        <Menu items={items} inlineIndent={16} className={styles.menu} mode="inline" selectedKeys={selectedMenuItem} />
        <SignOut isMobileMenu={true} />
        <MetaServerInfo deviceType={deviceType} />
      </>
    );
  };

  const renderMobileMenu = () => {
    const { documentDirection } = props;
    const closeIcon = (
      <DeprecatedFAIcon
        className="sidebar-panel__close"
        icon={DEPRECATED_FA_GLOBAL_SPRITE.x}
        size="lg"
        onClick={toggleMobileMenu}
      />
    );

    return (
      <Drawer
        placement={documentDirection === 'ltr' ? 'left' : 'right'}
        onClose={toggleMobileMenu}
        open={isMobileMenuVisible}
        keyboard
        closable={true}
        closeIcon={closeIcon}
        styles={drawerStyles}>
        <UserSocialProfile />
        {renderMenu(DEVICE_ENUM.MOBILE)}
      </Drawer>
    );
  };

  const toggleMobileMenu = (): void => {
    setIsMobileMenuVisible((visible) => !visible);
  };

  const { children, className } = props;
  const mdWidth = Number(styleVariables.md.replace('px', ''));

  return (
    <Layout>
      <UserPageHeader displayMobileMenu={toggleMobileMenu} />
      <Layout className={className}>
        <Responsive minWidth={mdWidth + 1}>
          <Sider collapsedWidth="0" theme="light" trigger={null} width={SIDER_WIDTH}>
            <UserSocialProfile />
            {renderMenu(DEVICE_ENUM.DESKTOP)}
          </Sider>
        </Responsive>
        <Responsive maxWidth={mdWidth}>{renderMobileMenu()}</Responsive>
        {children}
      </Layout>
      <AppFooter showFooterText />
    </Layout>
  );
});

const UserPageLayoutWithRouter = withRouterInnerRef(UserPageLayout);

export { UserPageLayoutWithRouter as UserPageLayout };
